import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Switch } from "./ui/switch";
import { Alert, AlertDescription } from "./ui/alert";
import {
  User,
  Settings,
  Shield,
  Users,
  Key,
  Bell,
  Edit2,
  Trash2,
  Plus,
  LogOut,
  CheckCircle,
  XCircle,
  Package as PackageIcon,
} from "lucide-react";

interface UserProfileProps {
  currentUser: {
    username: string;
    role: string;
    fullName: string;
    avatar: string;
  };
  onLogout: () => void;
}

interface SystemUser {
  id: number;
  username: string;
  fullName: string;
  role: string;
  email: string;
  lastLogin: string;
  status: string;
  avatar: string;
}

export function UserProfile({ currentUser, onLogout }: UserProfileProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({
    fullName: currentUser.fullName,
    email:
      currentUser.role === "Member"
        ? "member@example.com"
        : "admin@coworkinghub.com",
    phone: "+63 912 345 6789",
  });

  const [systemUsers, setSystemUsers] = useState<SystemUser[]>([
    {
      id: 1,
      username: "admin",
      fullName: "System Administrator",
      role: "System Admin",
      email: "admin@coworkinghub.com",
      lastLogin: "2024-06-18 09:30",
      status: "Active",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    },
    {
      id: 2,
      username: "manager",
      fullName: "Space Manager",
      role: "Space Manager",
      email: "manager@coworkinghub.com",
      lastLogin: "2024-06-18 08:15",
      status: "Active",
      avatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b605?w=150&h=150&fit=crop&crop=face",
    },
    {
      id: 3,
      username: "manager2",
      fullName: "Assistant Manager",
      role: "Space Manager",
      email: "manager2@coworkinghub.com",
      lastLogin: "2024-06-17 17:45",
      status: "Active",
      avatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    },
    {
      id: 4,
      username: "member",
      fullName: "John Doe",
      role: "Member",
      email: "john@example.com",
      lastLogin: "2024-06-18 14:20",
      status: "Active",
      avatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    },
  ]);

  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    activityAlerts: currentUser.role !== "Member",
    weeklyReports: currentUser.role !== "Member",
    systemMaintenance: currentUser.role === "System Admin",
    packageUpdates: currentUser.role === "Member",
    bookingReminders: currentUser.role === "Member",
  });

  const handleSaveProfile = () => {
    // Here you would typically save to backend
    setIsEditing(false);
    // Show success message
  };

  const toggleUserStatus = (userId: number) => {
    setSystemUsers((users) =>
      users.map((user) =>
        user.id === userId
          ? {
              ...user,
              status: user.status === "Active" ? "Inactive" : "Active",
            }
          : user
      )
    );
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "System Admin":
        return "bg-red-100 text-red-800";
      case "Space Manager":
        return "bg-blue-100 text-blue-800";
      case "Member":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status: string) => {
    return status === "Active"
      ? "bg-green-100 text-green-800"
      : "bg-red-100 text-red-800";
  };

  const permissions = {
    "System Admin": [
      "Full system access",
      "User management",
      "Financial reports",
      "System settings",
      "Data export",
      "Audit logs",
      "Tap In/Out management",
      "Client management",
      "Package management",
      "Usage tracking",
    ],
    "Space Manager": [
      "Tap In/Out management",
      "Client management",
      "Package management",
      "Usage tracking",
      "Basic reports",
      "Subscription management",
      "Profile settings",
    ],
    Member: [
      "View available packages",
      "Book workspace sessions",
      "View usage history",
      "Manage profile settings",
      "Update notification preferences",
      "Contact support",
    ],
  };

  // const memberTabs = ['profile', 'permissions', 'settings'];
  // const staffTabs = ['profile', 'users', 'permissions', 'settings'];
  // const availableTabs = currentUser.role === 'Member' ? memberTabs : staffTabs;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1>User Profile</h1>
        <p className="text-muted-foreground">
          {currentUser.role === "Member"
            ? "Manage your member profile and preferences"
            : "Manage your profile and system users"}
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList
          className={`grid w-full ${
            currentUser.role === "Member" ? "grid-cols-3" : "grid-cols-4"
          }`}
        >
          <TabsTrigger value="profile">My Profile</TabsTrigger>
          {currentUser.role !== "Member" && (
            <TabsTrigger value="users">User Management</TabsTrigger>
          )}
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* My Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Profile Information</CardTitle>
                  <CardDescription>
                    Manage your personal information and account details
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(!isEditing)}
                >
                  <Edit2 className="mr-2 h-4 w-4" />
                  {isEditing ? "Cancel" : "Edit"}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center space-x-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage
                    src={currentUser.avatar}
                    alt={currentUser.fullName}
                  />
                  <AvatarFallback>
                    {currentUser.fullName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg">{currentUser.fullName}</h3>
                    <Badge
                      className={getRoleColor(currentUser.role)}
                      variant="secondary"
                    >
                      {currentUser.role === "System Admin" ? (
                        <Shield className="mr-1 h-3 w-3" />
                      ) : currentUser.role === "Space Manager" ? (
                        <Users className="mr-1 h-3 w-3" />
                      ) : (
                        <User className="mr-1 h-3 w-3" />
                      )}
                      {currentUser.role}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    @{currentUser.username}
                  </p>
                  {currentUser.role === "Member" && (
                    <p className="text-sm text-green-600">
                      ✓ Active member since June 2024
                    </p>
                  )}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    value={editedProfile.fullName}
                    onChange={(e) =>
                      setEditedProfile({
                        ...editedProfile,
                        fullName: e.target.value,
                      })
                    }
                    disabled={!isEditing}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={editedProfile.email}
                    onChange={(e) =>
                      setEditedProfile({
                        ...editedProfile,
                        email: e.target.value,
                      })
                    }
                    disabled={!isEditing}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={editedProfile.phone}
                    onChange={(e) =>
                      setEditedProfile({
                        ...editedProfile,
                        phone: e.target.value,
                      })
                    }
                    disabled={!isEditing}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Input id="role" value={currentUser.role} disabled />
                </div>
              </div>

              {isEditing && (
                <div className="flex gap-2">
                  <Button onClick={handleSaveProfile}>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Security
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  Change Password
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  Two-Factor Authentication
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  Active Sessions
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {currentUser.role === "Member" ? (
                    <PackageIcon className="h-5 w-5" />
                  ) : (
                    <User className="h-5 w-5" />
                  )}
                  {currentUser.role === "Member" ? "Workspace" : "Account"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {currentUser.role === "Member" ? (
                  <>
                    <Button variant="outline" className="w-full justify-start">
                      My Bookings
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      Usage History
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      Contact Support
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" className="w-full justify-start">
                      Activity Log
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      Download Data
                    </Button>
                  </>
                )}
                <Button
                  variant="destructive"
                  className="w-full justify-start"
                  onClick={onLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* User Management Tab - Only for Staff */}
        {currentUser.role !== "Member" && (
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>System Users</CardTitle>
                    <CardDescription>
                      Manage system access and user accounts
                    </CardDescription>
                  </div>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {systemUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage
                                src={user.avatar}
                                alt={user.fullName}
                              />
                              <AvatarFallback>
                                {user.fullName
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{user.fullName}</p>
                              <p className="text-sm text-muted-foreground">
                                @{user.username}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={getRoleColor(user.role)}
                            variant="secondary"
                          >
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {user.lastLogin}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={getStatusColor(user.status)}
                            variant="secondary"
                          >
                            {user.status === "Active" ? (
                              <CheckCircle className="mr-1 h-3 w-3" />
                            ) : (
                              <XCircle className="mr-1 h-3 w-3" />
                            )}
                            {user.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Button variant="ghost" size="sm">
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleUserStatus(user.id)}
                            >
                              {user.status === "Active" ? (
                                <XCircle className="h-4 w-4" />
                              ) : (
                                <CheckCircle className="h-4 w-4" />
                              )}
                            </Button>
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Permissions Tab */}
        <TabsContent value="permissions" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {currentUser.role === "System Admin" ? (
                    <Shield className="h-5 w-5" />
                  ) : currentUser.role === "Space Manager" ? (
                    <Users className="h-5 w-5" />
                  ) : (
                    <User className="h-5 w-5" />
                  )}
                  {currentUser.role} Permissions
                </CardTitle>
                <CardDescription>
                  Your current access level and capabilities
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {permissions[
                    currentUser.role as keyof typeof permissions
                  ]?.map((permission, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">{permission}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Show other roles for staff to understand the system */}
            {currentUser.role !== "Member" && (
              <div className="grid gap-6 md:grid-cols-2">
                {Object.entries(permissions)
                  .filter(([role]) => role !== currentUser.role)
                  .map(([role, perms]) => (
                    <Card key={role}>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          {role === "System Admin" ? (
                            <Shield className="h-5 w-5" />
                          ) : role === "Space Manager" ? (
                            <Users className="h-5 w-5" />
                          ) : (
                            <User className="h-5 w-5" />
                          )}
                          {role}
                        </CardTitle>
                        <CardDescription>
                          Permissions for {role.toLowerCase()}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {perms.map((permission, index) => (
                            <div
                              key={index}
                              className="flex items-center space-x-2"
                            >
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="text-sm">{permission}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Settings
              </CardTitle>
              <CardDescription>
                Configure your notification preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(settings).map(([key, value]) => {
                // Hide certain settings based on user role
                if (
                  currentUser.role === "Member" &&
                  [
                    "activityAlerts",
                    "weeklyReports",
                    "systemMaintenance",
                  ].includes(key)
                ) {
                  return null;
                }
                if (
                  currentUser.role !== "Member" &&
                  ["packageUpdates", "bookingReminders"].includes(key)
                ) {
                  return null;
                }

                const getSettingLabel = (key: string) => {
                  const labels = {
                    emailNotifications: "Email Notifications",
                    pushNotifications: "Push Notifications",
                    activityAlerts: "Activity Alerts",
                    weeklyReports: "Weekly Reports",
                    systemMaintenance: "System Maintenance",
                    packageUpdates: "Package Updates",
                    bookingReminders: "Booking Reminders",
                  };
                  return labels[key as keyof typeof labels] || key;
                };

                const getSettingDescription = (key: string) => {
                  const descriptions = {
                    emailNotifications: "Receive notifications via email",
                    pushNotifications: "Browser push notifications",
                    activityAlerts: "Alerts for important system activities",
                    weeklyReports: "Weekly summary reports",
                    systemMaintenance: "System maintenance notifications",
                    packageUpdates: "Updates about new packages and offers",
                    bookingReminders: "Reminders about your bookings",
                  };
                  return descriptions[key as keyof typeof descriptions] || "";
                };

                return (
                  <div key={key} className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium">
                        {getSettingLabel(key)}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {getSettingDescription(key)}
                      </p>
                    </div>
                    <Switch
                      checked={value}
                      onCheckedChange={(checked) =>
                        setSettings((prev) => ({ ...prev, [key]: checked }))
                      }
                    />
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {currentUser.role === "System Admin" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  System Settings
                </CardTitle>
                <CardDescription>
                  Configure system-wide settings (System Admin only)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button variant="outline" className="w-full justify-start">
                  Database Backup
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  Export Data
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  System Logs
                </Button>
                <Button variant="destructive" className="w-full justify-start">
                  Reset System
                </Button>
              </CardContent>
            </Card>
          )}

          {currentUser.role !== "System Admin" && (
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                {currentUser.role === "Member"
                  ? "Additional settings are available for workspace bookings and payments."
                  : "Advanced system settings are only available to System Administrators."}
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
